/**
 * Firebase AI Logic (Gemini Live) client bootstrap.
 *
 * Lazily initialises `getAI` against Vertex AI in us-central1 (same region as
 * the Cloud Functions) and builds the `LiveGenerativeModel` used by the
 * real-time voice session. Tool declarations come from the SDK-neutral
 * `AGENT_TOOL_DECLARATIONS` in `@agente/shared` and are converted here into the
 * `firebase/ai` `Tool` shape — the tools themselves run 100% server-side via
 * the `executeAgentTool` callable; the client only relays function calls.
 */
import type { NeutralFunctionDeclaration, NeutralSchema } from '@agente/shared'
import {
  AGENT_TOOL_DECLARATIONS,
  isToolEnabled,
  VOICE_CONTEXT_TARGET_TOKENS,
  VOICE_CONTEXT_TRIGGER_TOKENS,
} from '@agente/shared'
import {
  type AI,
  type FunctionDeclaration,
  getAI,
  getLiveGenerativeModel,
  type LiveGenerationConfig,
  type LiveGenerativeModel,
  type LiveModelParams,
  type ObjectSchemaRequest,
  ResponseModality,
  type SchemaRequest,
  type Tool,
  VertexAIBackend,
} from 'firebase/ai'

import { app } from './firebase'

let aiClient: AI | null = null

/** Lazy singleton for the Firebase AI Logic client (Vertex AI, us-central1). */
function getAIClient(): AI {
  if (!aiClient) {
    aiClient = getAI(app, { backend: new VertexAIBackend('us-central1') })
  }
  return aiClient
}

/**
 * Converts a `NeutralSchema` (shared, SDK-agnostic) into the `SchemaRequest`
 * shape `firebase/ai` accepts. The neutral `type` values ('object' | 'string' |
 * 'number' | 'boolean' | 'array') are already valid OpenAPI `SchemaType`s.
 */
function toSchemaRequest(schema: NeutralSchema): SchemaRequest {
  const out: SchemaRequest = { type: schema.type }
  if (schema.description !== undefined) out.description = schema.description
  if (schema.enum !== undefined) out.enum = schema.enum
  if (schema.required !== undefined) out.required = schema.required
  if (schema.items !== undefined) out.items = toSchemaRequest(schema.items)
  if (schema.properties !== undefined) {
    out.properties = Object.fromEntries(
      Object.entries(schema.properties).map(([key, value]) => [key, toSchemaRequest(value)]),
    )
  }
  return out
}

function toFunctionDeclaration(decl: NeutralFunctionDeclaration): FunctionDeclaration {
  const fn: FunctionDeclaration = { name: decl.name, description: decl.description }
  if (decl.parameters) {
    // Top-level tool parameters are always `type: 'object'` in the shared declarations.
    fn.parameters = toSchemaRequest(decl.parameters) as ObjectSchemaRequest
  }
  return fn
}

/**
 * The shared agent tool declarations in the `firebase/ai` `Tool` shape.
 * `startEmailResearch` fica FORA da sessão de voz na v1: é uma operação longa,
 * em background, cujo fluxo (coletar período/foco + cartão de confirmação +
 * relatório na conversa) é do chat de texto.
 *
 * `disabledTools` remove as ferramentas desabilitadas na conversa de origem (o
 * modelo Live nem as enxerga); ausente/vazio ⇒ todas habilitadas.
 */
export function buildAgentTools(disabledTools?: readonly string[]): Tool[] {
  return [
    {
      functionDeclarations: AGENT_TOOL_DECLARATIONS.filter(
        (d) => d.name !== 'startEmailResearch' && isToolEnabled(d.name, disabledTools),
      ).map(toFunctionDeclaration),
    },
  ]
}

export interface CreateLiveModelConfig {
  model: string
  systemPrompt: string
  /**
   * Habilita a transcrição de entrada/saída (`inputAudioTranscription` /
   * `outputAudioTranscription`). Os modelos native-audio do Vertex
   * (`gemini-live-2.5-flash-native-audio` e o preview 09-2025) ACEITAM essa
   * config no handshake e devolvem `setupComplete` — DESDE QUE
   * `responseModalities` seja só AUDIO. O que quebrava o handshake (a causa real
   * de js-genai #1212, fechada em 2026-01-06) era incluir TEXT em
   * `responseModalities` junto da transcrição, não a transcrição em si.
   * DESLIGADA por padrão apenas como piso de degradação: se o `connect()` com
   * transcrição falhar, `useVoiceSession` reconecta sem ela e a persistência cai
   * no marcador de sessão. Verificado por handshake direto em 2026-07-06.
   */
  withTranscription?: boolean
  /** Anexa as ferramentas do agente (agenda, lembretes, clientes). Ligada por padrão. */
  withTools?: boolean
  /**
   * Habilita a compressão da janela de contexto (`contextWindowCompression`,
   * sliding window). Ligada por padrão: mantém a sessão longa dentro de um
   * orçamento de tokens. Fica atrás de uma flag (nova dimensão do handshake, em
   * simetria com `withTranscription`/`withTools`) porque o modelo native-audio
   * pode rejeitar a config no setup — o mesmo padrão de fallback do js-genai
   * #1212 já documentado acima. O hook tenta primeiro COM compressão e recua
   * para SEM compressão se o handshake falhar.
   */
  withCompression?: boolean
  /** Ferramentas desabilitadas na conversa (ids) — removidas das declarações. */
  disabledTools?: readonly string[]
  /**
   * Voz prebuilt do Gemini Live (`speechConfig.voiceConfig.prebuiltVoiceConfig.voiceName`).
   * Ausente → o modelo usa a voz default (Puck). O SDK NÃO expõe `languageCode`
   * no handshake — o idioma do áudio nativo é dirigido via system prompt.
   */
  voiceName?: string
}

/**
 * Builds the Live model for the voice session: respostas somente em áudio e,
 * conforme as flags, transcrição de entrada/saída e as ferramentas do agente. O
 * system prompt é o mesmo do chat de texto (com o adendo de voz).
 */
export function createLiveModel({
  model,
  systemPrompt,
  withTranscription = false,
  withTools = true,
  withCompression = true,
  voiceName,
  disabledTools,
}: CreateLiveModelConfig): LiveGenerativeModel {
  const generationConfig: LiveGenerationConfig = {
    // Somente AUDIO. NÃO adicionar ResponseModality.TEXT: TEXT junto da
    // transcrição quebra o handshake de setup em modelos native-audio (js-genai
    // #1212). A transcrição de entrada/saída (abaixo) é o caminho correto para
    // obter texto sem tocar em responseModalities.
    responseModalities: [ResponseModality.AUDIO],
  }
  if (withTranscription) {
    generationConfig.inputAudioTranscription = {}
    generationConfig.outputAudioTranscription = {}
  }
  if (withCompression) {
    generationConfig.contextWindowCompression = {
      triggerTokens: VOICE_CONTEXT_TRIGGER_TOKENS,
      slidingWindow: { targetTokens: VOICE_CONTEXT_TARGET_TOKENS },
    }
  }
  if (voiceName) {
    generationConfig.speechConfig = { voiceConfig: { prebuiltVoiceConfig: { voiceName } } }
  }
  const params: LiveModelParams = { model, generationConfig, systemInstruction: systemPrompt }
  if (withTools) {
    params.tools = buildAgentTools(disabledTools)
  }
  return getLiveGenerativeModel(getAIClient(), params)
}
